import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import LeagueGroupHeader from '../miniLeagues/LeagueGroupHeader';
import GameRow from './GameRow';
import { getGameStatus, FINISHED_STATUSES } from './gameStatus';

const isFinished = (game) => FINISHED_STATUSES.includes(getGameStatus(game));

/**
 * Whether this group opens closed.
 *
 * Collapsing every group by default would undo the point of the page: the weekly loop is
 * "have I picked yet", and a game you have to go looking for is a game you can miss. So a
 * group opens closed only when there is nothing in it to act on - the league has ended, or
 * every game in it has - and stays open whenever something is still being played.
 *
 * A group with nothing in it at all opens too, because the thing to read is the line
 * explaining why it is empty.
 */
export const startsCollapsed = (league, games) =>
  Boolean(league.cancelled || league.complete)
  || (games.length > 0 && games.every(isFinished));

/** What a collapsed group says about what is inside it, so closing it hides nothing. */
export const summarise = (games) => {
  if (games.length === 0) return null;
  const live = games.filter((game) => !isFinished(game)).length;
  const played = `${games.length} ${games.length === 1 ? 'game' : 'games'}`;
  if (live === 0) return `${played} · all finished`;
  return `${played} · ${live} running`;
};

function LeagueGroup({ league, games, currentUser, copiedCode, onCopyCode, onRemovePlayer, onViewGame, onViewLeague }) {
  const [collapsed, setCollapsed] = useState(() => startsCollapsed(league, games));
  const summary = summarise(games);

  return (
    <section className="game-group">
      <LeagueGroupHeader
        league={league}
        currentUser={currentUser}
        copiedCode={copiedCode}
        onCopyCode={onCopyCode}
        onViewLeague={onViewLeague}
      />

      {/* The toggle carries the summary rather than sitting next to one, so a collapsed
          group is a single line that both says what is inside and opens it. */}
      {games.length > 0 && (
        <button
          className="group-toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          <span>{collapsed ? summary : 'Hide games'}</span>
        </button>
      )}

      {games.length === 0 && (
        <p className="group-empty">
          {league.cancelled || league.complete
            ? 'Nothing left to show — its games have been archived.'
            : 'No games yet — the manager adds a tournament to start one.'}
        </p>
      )}

      {!collapsed && games.map((game) => (
        <GameRow
          key={game.id}
          game={game}
          leagueName={league.name}
          currentUser={currentUser}
          copiedCode={copiedCode}
          onCopyCode={onCopyCode}
          onRemovePlayer={onRemovePlayer}
          onViewGame={onViewGame}
        />
      ))}
    </section>
  );
}

export default LeagueGroup;
